import { useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';

interface CvReviewResult {
  id: string;
  status: string;
  cv_filename: string;
  overall_score: number | null;
  review_json: {
    overall_score: number;
    dimensions: Record<
      string,
      { score: number; strengths: string[]; gaps: string[] }
    >;
    candidate_summary: string;
    priority_actions: { immediate: string[]; week_1: string[]; week_2: string[] };
    hidden_assets: string[];
    final_note: string;
  } | null;
}

export function CvUpload() {
  usePageTitle('CV Assessor');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [review, setReview] = useState<CvReviewResult | null>(null);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && (dropped.type === 'application/pdf' || dropped.name.endsWith('.docx'))) {
      setFile(dropped);
      setError('');
    } else {
      setError('Please upload a PDF or DOCX file');
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const result = await api.post<{ id: string; status: string }>('/cv/upload', formData);
      setReviewId(result.id);

      // Start polling for results
      setPolling(true);
      pollForResults(result.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const pollForResults = async (id: string) => {
    let attempts = 0;
    const maxAttempts = 30; // 30 * 2s = 60s max

    const poll = async () => {
      attempts++;
      try {
        const result = await api.get<CvReviewResult>(`/cv/review/${id}`);
        if (result.status === 'complete' || result.status === 'failed') {
          setReview(result);
          setPolling(false);
          return;
        }
      } catch {
        // continue polling
      }

      if (attempts < maxAttempts) {
        setTimeout(poll, 2000);
      } else {
        setPolling(false);
        setError('Review is taking longer than expected. Check back later.');
      }
    };

    poll();
  };

  // Show review results
  if (review?.status === 'complete' && review.review_json) {
    const r = review.review_json;
    return (
      <Layout>
        <div className="max-w-3xl mx-auto mt-10 px-6 pb-20">
          <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">
            CV Review Results
          </h1>
          <p className="text-mustard-charcoal/60 mb-8">{review.cv_filename}</p>

          {/* Overall score */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full border-4 border-mustard-gold mb-3">
              <span className="font-display text-4xl font-bold text-mustard-gold">
                {r.overall_score}
              </span>
            </div>
            <p className="text-sm text-mustard-charcoal/60">out of 5</p>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-3">Summary</h2>
            <p className="text-sm text-mustard-charcoal leading-relaxed">{r.candidate_summary}</p>
          </div>

          {/* Dimensions */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-4">
              7-Point Framework Scores
            </h2>
            <div className="space-y-4">
              {Object.entries(r.dimensions).map(([key, dim]) => (
                <div key={key}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-mustard-charcoal capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm font-bold text-mustard-green-dark">
                      {dim.score}/5
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-mustard-green-dark h-2 rounded-full"
                      style={{ width: `${(dim.score / 5) * 100}%` }}
                    />
                  </div>
                  {dim.strengths.length > 0 && (
                    <div className="mt-2">
                      {dim.strengths.map((s, i) => (
                        <p key={i} className="text-xs text-green-700">+ {s}</p>
                      ))}
                    </div>
                  )}
                  {dim.gaps.length > 0 && (
                    <div className="mt-1">
                      {dim.gaps.map((g, i) => (
                        <p key={i} className="text-xs text-red-600">- {g}</p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Priority Actions */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
            <h2 className="font-display text-lg font-bold text-mustard-green-dark mb-4">
              Priority Actions
            </h2>
            {r.priority_actions.immediate.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-mustard-charcoal mb-2">Immediate</h3>
                <ul className="space-y-1">
                  {r.priority_actions.immediate.map((a, i) => (
                    <li key={i} className="text-sm text-mustard-charcoal/80">{a}</li>
                  ))}
                </ul>
              </div>
            )}
            {r.priority_actions.week_1.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-mustard-charcoal mb-2">Week 1</h3>
                <ul className="space-y-1">
                  {r.priority_actions.week_1.map((a, i) => (
                    <li key={i} className="text-sm text-mustard-charcoal/80">{a}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Final note */}
          {r.final_note && (
            <div className="bg-mustard-green-dark/5 rounded-xl p-6 border border-mustard-green-dark/10">
              <p className="text-sm text-mustard-green-dark italic">{r.final_note}</p>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto mt-20 px-6">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">
          CV Assessor
        </h1>
        <p className="text-mustard-charcoal/60 mb-8">
          Upload your CV for an AI-powered review against the LifeSpark360 7-Point Framework
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 text-sm">
            {error}
          </div>
        )}

        {polling ? (
          <div className="text-center py-16">
            <div className="inline-block w-12 h-12 border-4 border-mustard-green-dark/20 border-t-mustard-green-dark rounded-full animate-spin mb-4" />
            <p className="text-mustard-charcoal/70 font-medium">
              Analysing your CV...
            </p>
            <p className="text-sm text-mustard-charcoal/50 mt-2">
              This usually takes 15-30 seconds
            </p>
          </div>
        ) : (
          <>
            {/* Drop zone */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                dragActive
                  ? 'border-mustard-green-dark bg-mustard-green-dark/5'
                  : 'border-gray-300 hover:border-mustard-green-mid'
              }`}
            >
              {file ? (
                <div>
                  <p className="text-mustard-green-dark font-medium">{file.name}</p>
                  <p className="text-sm text-mustard-charcoal/50 mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={() => setFile(null)}
                    className="text-sm text-red-600 hover:underline mt-2"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-mustard-charcoal/70 mb-2">
                    Drag & drop your CV here, or
                  </p>
                  <label className="inline-block px-6 py-2 bg-mustard-green-dark text-white rounded-lg font-medium cursor-pointer hover:bg-mustard-green transition-colors">
                    Browse Files
                    <input
                      type="file"
                      accept=".pdf,.docx"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                  <p className="text-xs text-mustard-charcoal/40 mt-3">
                    PDF or DOCX, max 5MB
                  </p>
                </div>
              )}
            </div>

            {file && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="w-full mt-6 bg-mustard-gold text-mustard-green-dark py-3 rounded-lg font-bold hover:bg-mustard-gold-light transition-colors disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Analyse My CV'}
              </button>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
